import type { CSSProperties, ChangeEvent } from 'react';
import { useRef } from 'react';

import type { ButtonClick } from '@snowit/icetypes/events';
import type { IInput } from '../Input/Input';

import ButtonIcon from '@snowit/ui/Buttons/ButtonIcon';
import testId from '@snowit/utils/testId';

import Loader from '../../Loaders/LoaderSpinner';
import Typo from '../../Typography/Typo';

import cx from 'classnames';
import s from './InputColorPicker.module.scss';

export type IInputColorPicker = IInput & {
  onChange: (color: string, name?: string) => void;
  onReset?: () => void;
  classNames?: {
    wrapper?: string;
    wrapInput?: string;
    picker?: string;
    input?: string;
  };
};

const InputColorPicker = (props: IInputColorPicker) => {
  const {
    classNames,
    defaultValue,
    disabled,
    error,
    errorMsg,
    hint,
    id,
    label,
    loading,
    name,
    maxLength,
    onChange,
    onReset,
    placeholder,
    readOnly,
    required,
    size = 'tr',
    test,
    value,
  } = props;
  const ref = useRef<HTMLInputElement | null>(null);

  const handleClick = () => {
    if (disabled || loading) return;
    ref.current?.click();
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (disabled || loading) return;
    onChange(String(event.target.value ?? ''), name ?? id);
  };

  const handleReset = (event: ButtonClick) => {
    event.event.stopPropagation();
    if (typeof onReset === 'function') {
      onReset();
    }
  };

  const renderPicker = () => {
    if (loading) return null;

    return (
      <input
        className={cx(s.picker, classNames?.picker, { [s.noValue]: !value })}
        disabled={disabled || loading}
        id={id}
        name={name}
        onChange={handleChange}
        readOnly={readOnly}
        ref={ref}
        required={required}
        value={value ?? ''}
        defaultValue={defaultValue}
        maxLength={maxLength}
        type={'color'}
      />
    );
  };

  const renderInput = () => {
    return (
      <input
        className={cx(s.input, classNames?.input)}
        disabled={disabled || loading}
        placeholder={placeholder}
        readOnly
        onClick={handleClick}
        type={'text'}
        value={value ?? ''}
      />
    );
  };

  const renderLoader = () => {
    if (!loading) return null;
    return <Loader className={cx(s.loader)} size={16} />;
  };

  const renderReset = () => {
    if (!value) return null;
    if (!onReset) return null;

    return (
      <ButtonIcon
        classNames={{ button: s.btn }}
        icon={'close'}
        onClick={(event) => handleReset(event)}
        theme={'transparent'}
        size={size === 'db' ? 'xxl' : 'db'}
        color={'text'}
        rounded
      />
    );
  };

  const renderLabel = () => {
    if (!label) return null;
    if (size !== 'tr') return null;
    return <Typo className={s.label} text={label} tag={'label'} size={'df'} />;
  };

  const renderHint = () => {
    if (error && errorMsg) return null;
    if (!hint) return null;
    return <Typo className={s.hint} text={hint} tag={'p'} size={'md'} color={'text-l'} />;
  };

  const renderError = () => {
    if (!error) return null;
    if (!errorMsg) return null;
    if (error && !errorMsg) return null;
    return (
      <Typo
        className={s.hint}
        text={errorMsg}
        tag={'p'}
        size={'md'}
        color={'error'}
        {...testId(test, 'input_error')}
      />
    );
  };

  return (
    <div className={cx(s.wrapper, classNames?.wrapper)} {...testId(test, 'wrapper')}>
      <div
        className={cx(s.wrapInput, classNames?.wrapInput, {
          [s.required]: required,
          [s.readOnly]: readOnly || loading,
          [s.error]: error,
          [s.disabled]: disabled,
        })}
        style={{ '--l-h': `var(--s-${size})` } as CSSProperties}
        {...testId(test, 'container')}
        onClick={handleClick}
      >
        {renderLoader()}
        {renderPicker()}
        {renderInput()}
        {renderLabel()}
        {renderReset()}
      </div>
      {renderHint()}
      {renderError()}
    </div>
  );
};
export default InputColorPicker;
